import { useEffect, useState } from "react";
import { Sparkles, Wrench, RefreshCw, Download, ChevronDown, Smartphone } from "lucide-react";
import { isIOS, isStandalone } from "@/components/InstallButton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { APP_VERSION, CHANGELOG, isNewer } from "@/lib/changelog";
import { isNative } from "@/lib/native";

const SEEN_KEY = "aph_seen_version";
const TAGS = {
  new: ["New", Sparkles, "text-stellar border-stellar/30"],
  fix: ["Fixed", Wrench, "text-flare border-flare/30"],
  change: ["Changed", RefreshCw, "text-ash/70 border-rim"],
};

function Release({ r, testId }) {
  return (
    <div className="space-y-2.5" data-testid={testId}>
      <div className="flex items-baseline justify-between gap-3">
        <p className="font-branding font-semibold">{r.title}</p>
        <span className="font-mono text-[10px] text-ash/40 flex-shrink-0">v{r.version} · {r.date}</span>
      </div>
      <ul className="space-y-2">
        {r.items.map(([tag, text], i) => {
          const [label, Icon, tone] = TAGS[tag];
          return (
            <li key={i} className="flex gap-2.5 text-sm text-ash/80">
              <span className={`flex-shrink-0 h-fit flex items-center gap-1 text-[10px] uppercase tracking-wider font-branding border rounded-secure px-1.5 py-0.5 ${tone}`}><Icon size={11} strokeWidth={1.5} />{label}</span>
              <span>{text}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

const SITE = process.env.REACT_APP_BACKEND_URL;
const HOST = SITE.replace(/^https?:\/\//, "");
const isAndroid = () => /android/i.test(navigator.userAgent);
const go = (url) => { window.location.href = url; };
const reloadApp = () => navigator.serviceWorker?.getRegistration().then((r) => r?.update()).catch(() => {}).finally(() => window.location.reload());

function Card({ icon: Icon = Download, title, sub, action, onAction, testId, tone = "stellar" }) {
  const box = tone === "stellar" ? "bg-stellar/10 border-stellar/40" : "bg-void border-glass";
  return (
    <div className={`${box} border rounded-secure p-4 flex items-center gap-3`} data-testid={testId}>
      <Icon size={20} strokeWidth={1.5} className="text-stellar flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium" data-testid={`${testId}-title`}>{title}</p>
        <p className="text-xs text-ash/55">{sub}</p>
      </div>
      {action && <button onClick={onAction} data-testid={`${testId}-btn`} className="bg-stellar text-void font-branding font-semibold text-xs rounded-secure px-3 py-2 flex-shrink-0">{action}</button>}
    </div>
  );
}

function PlatformStatus({ latest }) {
  const apk = `${SITE}${latest?.apk || "/aphelion.apk"}`;
  if (latest && isNative()) return <Card testId="whatsnew-update-card" title={`Android update: v${latest.version}`} sub={`You have v${APP_VERSION}. Installs over it and keeps your vault.`} action="Update" onAction={() => go(apk)} />;
  if (latest) return <Card testId="whatsnew-reload-card" icon={RefreshCw} title={`Aphelion v${latest.version} is out`} sub={isIOS() ? "Reload to update your iPhone app instantly." : "Reload to update instantly."} action="Reload" onAction={reloadApp} />;
  if (isNative()) return <Card testId="whatsnew-uptodate-card" tone="muted" icon={Smartphone} title={`Android app v${APP_VERSION}: up to date`} sub="You'll be told here when a newer version is ready." />;
  if (isIOS()) return isStandalone()
    ? <Card testId="whatsnew-uptodate-card" tone="muted" icon={Smartphone} title={`iPhone app v${APP_VERSION}: up to date`} sub="Your Home Screen app updates itself, with no App Store needed." />
    : <Card testId="whatsnew-ios-card" icon={Smartphone} title={`Aphelion v${APP_VERSION} for iPhone`} sub="Add it to your Home Screen from Safari to get alerts and updates." action="How" onAction={() => go(`${SITE}/download`)} />;
  if (isAndroid()) return <Card testId="whatsnew-download-card" title={`Aphelion v${APP_VERSION} for Android`} sub={`${HOST}/aphelion.apk · installs over your current app`} action="Get it" onAction={() => go(apk)} />;
  return <Card testId="whatsnew-download-card" title={`Aphelion v${APP_VERSION} is ready to download`} sub={`Android & iPhone: ${HOST}/download`} action="Get it" onAction={() => window.open(`${SITE}/download`, "_blank", "noopener")} />;
}

function useLatestVersion() {
  const [latest, setLatest] = useState(null);
  useEffect(() => {
    const check = () => fetch(`${SITE}/version.json`, { cache: "no-store" })
      .then((r) => r.json())
      .then((v) => isNewer(v.version, APP_VERSION) && setLatest(v))
      .catch(() => {});
    const onVis = () => document.visibilityState === "visible" && check();
    check();
    const t = setInterval(check, 30 * 60 * 1000);
    document.addEventListener("visibilitychange", onVis);
    return () => { clearInterval(t); document.removeEventListener("visibilitychange", onVis); };
  }, []);
  return latest;
}

export function WhatsNewDialog({ open, onOpenChange }) {
  const latest = useLatestVersion();
  const [showAll, setShowAll] = useState(false);
  const [first, ...older] = CHANGELOG;

  useEffect(() => {
    if (localStorage.getItem(SEEN_KEY) !== APP_VERSION) onOpenChange(true);
  }, [onOpenChange]);
  useEffect(() => { if (latest) onOpenChange(true); }, [latest, onOpenChange]);

  const close = (o) => {
    if (!o) localStorage.setItem(SEEN_KEY, APP_VERSION);
    onOpenChange(o);
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent data-testid="whatsnew-dialog" className="bg-nebula border-rim text-ash sm:max-w-md rounded-secure max-h-[85vh] overflow-y-auto overflow-x-hidden w-[calc(100%-2rem)] [&>*]:min-w-0">
        <DialogHeader>
          <DialogTitle className="font-branding flex items-center gap-2"><Sparkles size={18} strokeWidth={1.5} className="text-stellar" /> What's new in Aphelion</DialogTitle>
          <DialogDescription className="text-ash/50">Version {APP_VERSION}: fixes, changes and new features.</DialogDescription>
        </DialogHeader>
        <PlatformStatus latest={latest} />
        <div className="bg-void border border-glass rounded-secure p-4"><Release r={first} testId="whatsnew-latest" /></div>
        {showAll ? (
          <div className="space-y-5 px-1" data-testid="whatsnew-older">
            {older.map((r) => <Release key={r.version} r={r} testId={`whatsnew-release-${r.version}`} />)}
          </div>
        ) : (
          <button onClick={() => setShowAll(true)} data-testid="whatsnew-show-older-btn" className="flex items-center justify-center gap-1.5 text-xs text-ash/70 hover:text-stellar"><ChevronDown size={14} strokeWidth={1.5} /> Earlier updates</button>
        )}
        <button onClick={() => close(false)} data-testid="whatsnew-close-btn" className="w-full bg-stellar text-void font-branding font-semibold rounded-secure py-2.5 text-sm hover:opacity-85 transition-opacity">Got it</button>
      </DialogContent>
    </Dialog>
  );
}
